import { useState, useRef, useEffect } from 'react';
import { KeenIcon } from '@/components';
import { CrudAvatarUpload } from '@/partials/crud';
import { Modal } from '@/components';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import * as Yup from 'yup';
import type { IImageInputFile } from '@/components/image-input';
import { textFieldValidation } from '@/utils/validations';
import { ProfileService } from '@/services/ProfileService';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import { setProfilePic } from '@/store/authSlice';
import { useDispatch } from 'react-redux';

const nameValidationSchema = Yup.object().shape({
  name: textFieldValidation('Name', 2, 50, true),
});

const PersonalInfo = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [avatar, setAvatar] = useState<IImageInputFile[]>([]);
  const [isAvatarUpdating, setIsAvatarUpdating] = useState(false);
  const dispatch = useDispatch();
  const {
    data: profileData,
    isLoading,
    error: fetchError,
  } = ProfileService.useGetProfileQuery();
  const [updateProfile] = ProfileService.useUpdateProfileMutation();

  // Notification preferences state and API hooks
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [bookingNotifications, setBookingNotifications] = useState(false);
  const [paymentNotifications, setPaymentNotifications] = useState(false);
  const [harbourNotifications, setHarbourNotifications] = useState(false);
  const [clubNotifications, setClubNotifications] = useState(false);
  const [feedbackNotifications, setFeedbackNotifications] = useState(false);
  const [postCommunityNotifications, setPostCommunityNotifications] =
    useState(false);
  const [subscriptionNotifications, setSubscriptionNotifications] =
    useState(false);
  const [systemNotifications, setSystemNotifications] = useState(false);
  const [kycNotifications, setKycNotifications] = useState(false);
  const {
    data: notificationPrefsData,
    isLoading: isNotificationPrefsLoading,
    error: notificationPrefsError,
  } = ProfileService.useGetNotificationPreferencesQuery();
  const [updateNotificationPreferences] =
    ProfileService.useUpdateNotificationPreferencesMutation();

  // Set initial profile data
  useEffect(() => {
    if (profileData?.status && profileData.data) {
      setName(profileData.data.name || '');
      setEmail(profileData.data.email || '');
      setNewName(profileData.data.name || '');
      if (profileData.data.profilePicture) {
        setAvatar([{ dataURL: profileData.data.profilePicture }]);
        dispatch(
          setProfilePic({ profile_picture: profileData.data.profilePicture })
        );
      } else {
        setAvatar([]);
        dispatch(setProfilePic({ profile_picture: '' }));
      }
    }
    if (fetchError) {
      enqueueSnackbar('Failed to load profile data', { variant: 'error' });
    }
  }, [profileData, fetchError, enqueueSnackbar, dispatch]);

  // Set initial notification preferences data
  useEffect(() => {
    if (notificationPrefsData) {
      setEmailNotifications(notificationPrefsData.has_email_notifications);
      setPushNotifications(notificationPrefsData.has_push_notifications);
      setBookingNotifications(
        notificationPrefsData.is_booking_notification_enabled
      );
      setPaymentNotifications(
        notificationPrefsData.is_payment_notification_enabled
      );
      setHarbourNotifications(
        notificationPrefsData.is_harbour_notification_enabled
      );
      setClubNotifications(notificationPrefsData.is_club_notification_enabled);
      setFeedbackNotifications(
        notificationPrefsData.is_feedback_notification_enabled
      );
      setPostCommunityNotifications(
        notificationPrefsData.is_post_community_notification_enabled
      );
      setSubscriptionNotifications(
        notificationPrefsData.is_subscription_notification_enabled
      );
      setSystemNotifications(
        notificationPrefsData.is_system_notification_enabled
      );
      setKycNotifications(notificationPrefsData.is_kyc_notification_enabled);
    }
    if (notificationPrefsError) {
      enqueueSnackbar('Failed to load notification preferences', {
        variant: 'error',
      });
    }
  }, [notificationPrefsData, notificationPrefsError, enqueueSnackbar]);

  const hasNameChanged = newName.trim() !== name.trim();

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setNewName(name);
    setError('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setError('');
  };

  const handleSave = async () => {
    // Skip API call if name hasn't changed
    if (!hasNameChanged) {
      setIsModalOpen(false);
      return;
    }

    try {
      await nameValidationSchema.validate(
        { name: newName },
        { abortEarly: false }
      );
      setError('');

      const response = await updateProfile({ name: newName }).unwrap();

      if (response.status && response.data) {
        setName(response.data.name);
        setIsModalOpen(false);
        enqueueSnackbar(response.message, { variant: 'success' });
      } else {
        throw new Error(response.message || 'Failed to update name');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Yup.ValidationError
          ? err.errors[0]
          : err instanceof Error
            ? err.message
            : 'An error occurred';
      setError(errorMessage);
      setIsModalOpen(false);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleAvatarChange = async (file: File | null) => {
    // Skip API call if no change is needed
    if (file === null && avatar.length === 0) {
      return; // No avatar to remove
    }
    if (
      file &&
      avatar.length > 0 &&
      avatar[0].file &&
      avatar[0].file.name === file.name &&
      avatar[0].file.size === file.size
    ) {
      return; // Same file selected
    }
    setIsAvatarUpdating(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('profilePicture', file);
      } else {
        formData.append('profilePicture', ''); // Send empty string for removal
      }

      const response = await updateProfile(formData).unwrap();

      if (response.status) {
        if (file) {
          setAvatar([{ file, dataURL: URL.createObjectURL(file) }]);
          dispatch(
            setProfilePic({ profile_picture: response.data.profilePicture })
          );
          enqueueSnackbar(response.message, {
            variant: 'success',
          });
        } else {
          if (avatar[0]?.dataURL) {
            URL.revokeObjectURL(avatar[0].dataURL); // Clean up object URL
          }
          setAvatar([]);
          dispatch(setProfilePic({ profile_picture: '' }));
          enqueueSnackbar('Profile picture removed successfully', {
            variant: 'success',
          });
        }
      } else {
        throw new Error(response.message || 'Failed to update profile picture');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsAvatarUpdating(false);
    }
  };

  const handleNotificationPreferenceChange = async (
    field:
      | 'has_email_notifications'
      | 'has_push_notifications'
      | 'is_booking_notification_enabled'
      | 'is_payment_notification_enabled'
      | 'is_harbour_notification_enabled'
      | 'is_club_notification_enabled'
      | 'is_feedback_notification_enabled'
      | 'is_post_community_notification_enabled'
      | 'is_subscription_notification_enabled'
      | 'is_system_notification_enabled'
      | 'is_kyc_notification_enabled',
    value: boolean
  ) => {
    try {
      const updatedPreferences = {
        has_email_notifications: emailNotifications,
        has_push_notifications: pushNotifications,
        is_booking_notification_enabled: bookingNotifications,
        is_payment_notification_enabled: paymentNotifications,
        is_harbour_notification_enabled: harbourNotifications,
        is_club_notification_enabled: clubNotifications,
        is_feedback_notification_enabled: feedbackNotifications,
        is_post_community_notification_enabled: postCommunityNotifications,
        is_subscription_notification_enabled: subscriptionNotifications,
        is_system_notification_enabled: systemNotifications,
        is_kyc_notification_enabled: kycNotifications,
      };

      updatedPreferences[field] = value;

      const response =
        await updateNotificationPreferences(updatedPreferences).unwrap();

      // Update state with the response values
      setEmailNotifications(response.has_email_notifications);
      setPushNotifications(response.has_push_notifications);
      setBookingNotifications(response.is_booking_notification_enabled);
      setPaymentNotifications(response.is_payment_notification_enabled);
      setHarbourNotifications(response.is_harbour_notification_enabled);
      setClubNotifications(response.is_club_notification_enabled);
      setFeedbackNotifications(response.is_feedback_notification_enabled);
      setPostCommunityNotifications(
        response.is_post_community_notification_enabled
      );
      setSubscriptionNotifications(
        response.is_subscription_notification_enabled
      );
      setSystemNotifications(response.is_system_notification_enabled);
      setKycNotifications(response.is_kyc_notification_enabled);
      enqueueSnackbar('Notification preferences updated successfully', {
        variant: 'success',
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });

      // Revert the change on error
      switch (field) {
        case 'has_email_notifications':
          setEmailNotifications(!value);
          break;
        case 'has_push_notifications':
          setPushNotifications(!value);
          break;
        case 'is_booking_notification_enabled':
          setBookingNotifications(!value);
          break;
        case 'is_payment_notification_enabled':
          setPaymentNotifications(!value);
          break;
        case 'is_harbour_notification_enabled':
          setHarbourNotifications(!value);
          break;
        case 'is_club_notification_enabled':
          setClubNotifications(!value);
          break;
        case 'is_feedback_notification_enabled':
          setFeedbackNotifications(!value);
          break;
        case 'is_post_community_notification_enabled':
          setPostCommunityNotifications(!value);
          break;
        case 'is_subscription_notification_enabled':
          setSubscriptionNotifications(!value);
          break;
        case 'is_system_notification_enabled':
          setSystemNotifications(!value);
          break;
        case 'is_kyc_notification_enabled':
          setKycNotifications(!value);
          break;
      }
    }
  };

  if (isLoading || isNotificationPrefsLoading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div className="card min-w-full">
      <h1 className="sr-only" id="personal-info-page-heading">
        Personal Information
      </h1>
      <div className="card-header">
        <h2 id="personal-info-heading" className="card-title" tabIndex={-1}>
          Personal Info
        </h2>
      </div>
      <div className="card-table scrollable-x-auto pb-3">
        <table className="table align-middle text-sm text-gray-500">
          <tbody>
            <tr>
              <td className="py-2 min-w-28 text-gray-600 font-normal">Photo</td>
              <td className="py-2 text-gray-700 font-normal min-w-32 text-2sm">
                150x150px JPEG, PNG Im age (max 5MB)
              </td>
              <td className="py-2 text-center">
                <div className="flex justify-center items-center">
                  <CrudAvatarUpload
                    onFileSelect={handleAvatarChange}
                    setAvatar={setAvatar}
                    avatar={avatar}
                    isLoading={isAvatarUpdating}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">Name</td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                {name || 'Not set'}
              </td>
              <td className="py-2 text-center">
                <button
                  onClick={handleOpenModal}
                  className="btn btn-sm btn-icon btn-clear btn-primary"
                  aria-label="Edit name"
                >
                  <KeenIcon
                    icon="notepad-edit"
                    className="contrast:text-white"
                  />
                </button>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">Email</td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                {email || 'Not set'}
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Email Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="email-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Email Notifications"
                >
                  <input
                    type="checkbox"
                    id="email-notifications"
                    checked={emailNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'has_email_notifications',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Email Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {emailNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Mobile Push Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="push-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Mobile Push Notifications"
                >
                  <input
                    type="checkbox"
                    id="push-notifications"
                    checked={pushNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'has_push_notifications',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Mobile Push Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {pushNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Booking Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="booking-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Booking Notifications"
                >
                  <input
                    type="checkbox"
                    id="booking-notifications"
                    checked={bookingNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_booking_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Booking Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {bookingNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Payment Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="payment-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Payment Notifications"
                >
                  <input
                    type="checkbox"
                    id="payment-notifications"
                    checked={paymentNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_payment_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Payment Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {paymentNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Harbour Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="harbour-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Harbour Notifications"
                >
                  <input
                    type="checkbox"
                    id="harbour-notifications"
                    checked={harbourNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_harbour_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Harbour Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {harbourNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Club Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="club-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Club Notifications"
                >
                  <input
                    type="checkbox"
                    id="club-notifications"
                    checked={clubNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_club_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Club Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {clubNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Feedback Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="feedback-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Feedback Notifications"
                >
                  <input
                    type="checkbox"
                    id="feedback-notifications"
                    checked={feedbackNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_feedback_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Feedback Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {feedbackNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Community Post Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="post-community-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Community Post Notifications"
                >
                  <input
                    type="checkbox"
                    id="post-community-notifications"
                    checked={postCommunityNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_post_community_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Community Post Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {postCommunityNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                Subscription Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="subscription-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="Subscription Notifications"
                >
                  <input
                    type="checkbox"
                    id="subscription-notifications"
                    checked={subscriptionNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_subscription_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="Subscription Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {subscriptionNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                System Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="system-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="System Notifications"
                >
                  <input
                    type="checkbox"
                    id="system-notifications"
                    checked={systemNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_system_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="System Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {systemNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-gray-600 font-normal">
                KYC Notifications
              </td>
              <td className="py-2 text-gray-800 font-normal text-sm">
                <label
                  htmlFor="kyc-notifications"
                  className="flex items-center cursor-pointer"
                  aria-label="KYC Notifications"
                >
                  <input
                    type="checkbox"
                    id="kyc-notifications"
                    checked={kycNotifications}
                    onChange={(e) =>
                      handleNotificationPreferenceChange(
                        'is_kyc_notification_enabled',
                        e.target.checked
                      )
                    }
                    className="checkbox checkbox-sm mr-2 cursor-pointer"
                    aria-label="KYC Notifications"
                  />
                  <span className="text-sm cursor-pointer">
                    {kycNotifications ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        className="!flex items-center justify-center"
        aria-labelledby="edit-name-modal-title"
        aria-describedby="edit-name-modal-description"
      >
        <div className="bg-gray-100 rounded-lg p-6 w-full max-w-md mx-4">
          <h2
            id="edit-name-modal-title"
            className="text-lg font-medium text-gray-900 mb-4"
          >
            Edit Name
          </h2>
          <div id="edit-name-modal-description" className="sr-only">
            Form to edit your name with save and cancel options
          </div>
          <div className="mb-4">
            <label
              htmlFor="name-input"
              className="block text-sm font-medium text-gray-700 mb-1"
              aria-label="Name"
            >
              Name
            </label>
            <input
              ref={inputRef}
              id="name-input"
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setError('');
              }}
              className={clsx(
                'input input-bordered w-full',
                error && 'border-red-500 focus:ring-red-500'
              )}
              aria-invalid={!!error}
              aria-describedby={error ? 'name-error' : undefined}
              placeholder="Enter your name"
              onKeyDown={preventLeadingWhitespace}
            />
            {error && (
              <p id="name-error" className="text-red-500 text-sm mt-1">
                {error}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={handleCloseModal}
              className="btn btn-sm btn-light"
              aria-label="Cancel name edit"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className={clsx(
                'btn btn-sm btn-primary',
                !hasNameChanged && 'btn-disabled opacity-50 cursor-not-allowed'
              )}
              disabled={!hasNameChanged}
              aria-label="Save name changes"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export { PersonalInfo };
